import { useState } from 'react';
import type { Period } from '@/entities/types';
import { useReportQueryError } from '@/hooks/useReportQueryError';
import { useGoalsCompanion } from '../../../hooks/useGoalsCompanion';
import { MessageInput } from './MessageInput';
import { MessagesList } from './MessagesList';

interface AiPanelProps {
  period: Period;
  targetStartAt: Date;
  className?: string;
}

export function AiPanel({ className, period, targetStartAt }: AiPanelProps) {
  const {
    conversationId,
    messages,
    sendMessage,
    isLoading,
    sendError,
    isSending,
    acceptSuggestedTaskByTarget,
    skipSuggestedTaskByTarget,
  } = useGoalsCompanion({
    period,
    targetStartAt,
  });

  const [actionError, setActionError] = useState<Error | null>(null);
  const [draftsByConversation, setDraftsByConversation] = useState<
    Record<string, string>
  >({});

  const errorMessage =
    (sendError ?? actionError)
      ? 'I\u2019m having trouble right now. Please try again.'
      : undefined;

  useReportQueryError(sendError, 'AiPanel');

  async function handleAddToPlan(
    messageId: string,
    blockIndex: number,
  ): Promise<void> {
    setActionError(null);
    try {
      await acceptSuggestedTaskByTarget({ messageId, blockIndex });
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error
          : new Error('Failed to add task to plan'),
      );
    }
  }

  async function handleSkip(
    messageId: string,
    blockIndex: number,
  ): Promise<void> {
    setActionError(null);
    try {
      await skipSuggestedTaskByTarget({ messageId, blockIndex });
    } catch (error) {
      setActionError(
        error instanceof Error ? error : new Error('Failed to skip task'),
      );
    }
  }

  function handleDraftChange(nextMessage: string): void {
    setDraftsByConversation((previousDrafts) => ({
      ...previousDrafts,
      [conversationId]: nextMessage,
    }));
  }

  function handleSendMessage(content: string): void {
    setDraftsByConversation((previousDrafts) => ({
      ...previousDrafts,
      [conversationId]: '',
    }));
    sendMessage(content);
  }

  const currentDraft = draftsByConversation[conversationId] ?? '';

  return (
    <div className={`flex min-h-0 min-w-0 flex-col ${className || ''}`}>
      <MessagesList
        className="-mr-5 min-h-0 flex-1 overflow-y-auto pr-5"
        messages={messages}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onAddToPlan={(messageId, blockIndex) =>
          void handleAddToPlan(messageId, blockIndex)
        }
        onSkip={(messageId, blockIndex) =>
          void handleSkip(messageId, blockIndex)
        }
        disabled={isSending}
      />
      <MessageInput
        onSendMessage={handleSendMessage}
        message={currentDraft}
        onMessageChange={handleDraftChange}
        className="mt-2"
        disabled={isSending}
      />
    </div>
  );
}
